import React, { useEffect, useState } from 'react';

/**
 * ScoreGauge Component
 * Animated horizontal bar gauge showing score percentage with color coding
 * 
 * @param {string} label - Gauge label (e.g., "Top of Funnel")
 * @param {string} description - Gauge description
 * @param {number} percentage - Score percentage (0-100)
 * @param {number} score - Actual score value
 * @param {number} maxScore - Maximum possible score
 * @param {number} delay - Animation delay in ms (for staggered effect)
 */
export default function ScoreGauge({ 
  label, 
  description, 
  percentage, 
  score, 
  maxScore,
  delay = 0 
}) {
  const [animatedWidth, setAnimatedWidth] = useState(0);
  
  // Trigger animation after mount with delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedWidth(percentage);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [percentage, delay]);
  
  // Determine color based on percentage thresholds
  const getColor = () => {
    if (percentage <= 40) {
      return {
        bg: 'bg-leak-red',
        text: 'text-leak-red',
        icon: '🔴',
        label: 'Needs Attention'
      };
    } else if (percentage <= 70) {
      return {
        bg: 'bg-caution-yellow',
        text: 'text-caution-yellow',
        icon: '⚠️',
        label: 'Room for Improvement'
      };
    } else {
      return {
        bg: 'bg-healthy-green',
        text: 'text-healthy-green',
        icon: '✅',
        label: 'Healthy'
      };
    }
  };
  
  const colorScheme = getColor();
  
  return (
    <div className="mb-3 sm:mb-4">
      {/* Label and Score */}
      <div className="flex items-center justify-between mb-1.5 sm:mb-2">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white">
            {label}
          </h3>
          <span className="text-base sm:text-lg md:text-xl" aria-hidden="true">
            {colorScheme.icon}
          </span>
        </div>
        <div className="text-right">
          <span className={`text-lg sm:text-xl md:text-2xl font-bold ${colorScheme.text}`}>
            {percentage}%
          </span>
          <span className="text-gray-400 text-xs sm:text-sm ml-1.5 sm:ml-2">
            ({score}/{maxScore})
          </span>
        </div>
      </div>
      
      {/* Description */}
      <p className="text-xs sm:text-sm text-gray-400 mb-2 sm:mb-3">
        {description}
      </p>
      
      {/* Progress Bar */}
      <div className="w-full bg-gray-700 h-2.5 sm:h-3 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorScheme.bg} transition-all duration-800 ease-out`}
          style={{ width: `${animatedWidth}%` }}
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-label={`${label}: ${percentage}% - ${colorScheme.label}`}
        />
      </div>
      
      {/* Status Label */}
      <p className={`text-xs ${colorScheme.text} font-medium mt-0.5 sm:mt-1`}>
        {colorScheme.label}
      </p>
    </div>
  );
}

